import { z } from 'zod'
import {
  CouncilDecryptionModeSchema,
  CouncilLinkOriginSchema,
  CouncilStatusSchema,
  ElectionStatusSchema,
} from '../enums'
import { ResultsWaitSchema } from '../domain/results-wait'
import { EthAddressSchema, IsoDateTimeSchema, UuidSchema } from '../primitives'

/**
 * Election committees ("councils"): Council ceremonies (davinci-dkg-council) linked to an organization so its votings
 * can use `keyMode: 'council'`. A committee is created in the Council app (another origin) and joined to the
 * organization by a one-use pairing code (recommended) or by pasting its link (expert fallback, which always ends in
 * a fingerprint confirmation). Nothing either path delivers is trusted: the server verifies every committee on chain
 * (phase, grants, policy) and keeps its status fresh (process sync). Members' keys never exist on this origin.
 *
 * Organizer routes: `/api/org/organizations/:orgId/councils[...]`. Public (Council app, rate-limited):
 * `/api/public/council-pairing/:code[/complete]` (docs/pairing-api.md).
 */

/** Council ceremony id: `bytes12` hex (`0x` + 24 hex digits). Stored and returned lowercase. */
export const CeremonyIdSchema = z
  .string()
  .trim()
  .regex(/^0x[0-9a-fA-F]{24}$/, 'Expected a committee id (0x followed by 24 hex digits)')
  .transform((v) => v.toLowerCase())
export type CeremonyId = z.output<typeof CeremonyIdSchema>

/**
 * Pairing code: 12 Crockford base-32 symbols (60 bits), shown grouped `XXXX-XXXX-XXXX`. Input is forgiving
 * (`normalizePairingCode`): case, spaces and dashes are ignored, O reads as 0 and I / L as 1.
 */
export const PAIRING_CODE_ALPHABET = '0123456789ABCDEFGHJKMNPQRSTVWXYZ'
export const PAIRING_CODE_LENGTH = 12
/** Minutes a pairing code stays valid. */
export const PAIRING_CODE_TTL_MINUTES = 60

export function normalizePairingCode(input: string): string | null {
  const s = input
    .toUpperCase()
    .replace(/[\s-]+/g, '')
    .replace(/O/g, '0')
    .replace(/[IL]/g, '1')
  if (s.length !== PAIRING_CODE_LENGTH) return null
  for (const c of s) if (!PAIRING_CODE_ALPHABET.includes(c)) return null
  return s
}

/** `XXXXXXXXXXXX` → `XXXX-XXXX-XXXX`. */
export const formatPairingCode = (code: string): string => code.match(/.{1,4}/g)?.join('-') ?? code

export const PairingCodeParamSchema = z
  .string()
  .max(40)
  .transform((v, ctx) => {
    const n = normalizePairingCode(v)
    if (!n) {
      ctx.addIssue({ code: 'custom', message: 'Invalid pairing code' })
      return z.NEVER
    }
    return n
  })

/* ================================================================== stored committee */

/** The committee's decryption opening (immutable on chain except `openedAt`). Dates are when opening is POSSIBLE. */
export const CouncilPolicySchema = z.object({
  mode: CouncilDecryptionModeSchema,
  /** Scheduled: from when the opening is possible. Null for manual. */
  opensAt: IsoDateTimeSchema.nullable(),
  /** Manual: the date the opening becomes possible anyway if nobody opens earlier; null = no backstop date. */
  fallbackAt: IsoDateTimeSchema.nullable(),
  /** Manual: when the committee's organizer opened it (null until then). */
  openedAt: IsoDateTimeSchema.nullable(),
  /** The decryption gate is open right now (members may turn their keys). */
  open: z.boolean(),
})
export type CouncilPolicy = z.infer<typeof CouncilPolicySchema>

export const CEREMONY_PHASES = ['registration', 'dealing', 'live', 'aborted', 'unknown'] as const
export const CeremonyPhaseSchema = z.enum(CEREMONY_PHASES)
export type CeremonyPhase = z.infer<typeof CeremonyPhaseSchema>

export const CouncilVotingSchema = z.object({
  id: UuidSchema,
  title: z.string(),
  status: ElectionStatusSchema,
  /** After the end: the results wait for the committee (opening / keys), else null. */
  resultsWait: ResultsWaitSchema.nullable(),
})
export type CouncilVoting = z.infer<typeof CouncilVotingSchema>

export const CouncilSchema = z.object({
  id: UuidSchema,
  /** Ceremony id (bytes12 hex, lowercase): the committee's fingerprint, with t / n / members / creation date. */
  cid: z.string(),
  /** Display name (organization-local). */
  label: z.string(),
  status: CouncilStatusSchema,
  /** Plain-words reason while not ready (e.g. "finish the 'Connect to DAVINCI Elections' step"), else null. */
  statusReason: z.string().nullable(),
  phase: CeremonyPhaseSchema,
  /** t: members needed to unlock results (known from creation; null until first read). */
  threshold: z.number().int().min(1).nullable(),
  /** n: committee size, fixed when registration closes (null before). */
  size: z.number().int().min(1).nullable(),
  /** Invitations sent by the committee's organizer (the size bound while registering). */
  invited: z.number().int().min(0).nullable(),
  joinedCount: z.number().int().min(0),
  dealtCount: z.number().int().min(0),
  grants: z.object({
    /** The registry's Council adapter is allowed on this ceremony. */
    adapterAllowed: z.boolean(),
    /** This organization's creator address is authorized on this ceremony. */
    creatorAuthorized: z.boolean(),
  }),
  policy: CouncilPolicySchema.nullable(),
  /** Ceremony creation (block time of CeremonyCreated); null when not found yet. */
  createdOnChainAt: IsoDateTimeSchema.nullable(),
  linkOrigin: CouncilLinkOriginSchema,
  /** Pasted links: when the organizer confirmed the fingerprint (pairing-linked committees are confirmed at link). */
  fingerprintConfirmedAt: IsoDateTimeSchema.nullable(),
  /** A pasted committee cannot be selected until its fingerprint is confirmed. */
  needsFingerprintConfirmation: z.boolean(),
  /** Can be chosen for a new voting: ready / open AND confirmed. */
  selectable: z.boolean(),
  /** Votings of this organization bound to the committee (newest first). */
  linkedVotings: z.array(CouncilVotingSchema),
  /** `${COUNCIL_APP_URL}/c/${cid}` (open the committee in the Council app); null when the app URL is not set. */
  appUrl: z.string().nullable(),
  lastCheckedAt: IsoDateTimeSchema.nullable(),
  createdAt: IsoDateTimeSchema,
})
export type Council = z.infer<typeof CouncilSchema>

export const CouncilResponseSchema = z.object({ council: CouncilSchema })
export type CouncilResponse = z.infer<typeof CouncilResponseSchema>

/**
 * A committee being set up in the Council app, from the moment the organizer starts it here ("Create a committee")
 * until it is connected with a pairing code. Elections knows only the name the organizer chose (it pre-fills the
 * Council app's own name field); nothing of it is on chain yet.
 */
export const CouncilSetupSchema = z.object({
  id: UuidSchema,
  /** The name the organizer chose (shown on the committee once connected). */
  label: z.string(),
  createdAt: IsoDateTimeSchema,
  /** Not connected after a week: probably abandoned (the organizer may remove it). */
  stale: z.boolean(),
  /** Council app deep link that starts this committee there (`/new?davinci=v1&label=…`). */
  createUrl: z.string().nullable(),
})
export type CouncilSetup = z.infer<typeof CouncilSetupSchema>

/** A committee name as people type it: trimmed, single spaces, no control characters. */
export const CommitteeLabelSchema = z
  .string()
  .transform((v) =>
    v
      .replace(/[\u0000-\u001f\u007f-\u009f]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
  )
  .pipe(z.string().min(1).max(80))

/** POST …/councils/setups `{ label }` → CouncilSetupResponse (201). */
export const CouncilSetupRequestSchema = z.object({ label: CommitteeLabelSchema })
export type CouncilSetupRequest = z.input<typeof CouncilSetupRequestSchema>
export const CouncilSetupResponseSchema = z.object({ setup: CouncilSetupSchema })
export type CouncilSetupResponse = z.infer<typeof CouncilSetupResponseSchema>

/* GET /api/org/organizations/:orgId/councils → CouncilListResponse (newest first). */
export const CouncilListResponseSchema = z.object({
  councils: z.array(CouncilSchema),
  /** Committees started here and not connected yet (newest first; removed or connected ones are not listed). */
  setups: z.array(CouncilSetupSchema),
  /** False ⇒ committee features are hidden (COUNCIL_APP_URL unset) or the council subsystem failed closed. */
  available: z.boolean(),
  /** Why committees are unavailable (plain words), null when available. */
  unavailableReason: z.string().nullable(),
  /** Council app origin + `/new?davinci=v1&label=…` deep link for "Create a committee" (null when unavailable). */
  createUrl: z.string().nullable(),
  /** The organization's creator address (what the committee's organizer authorizes; shown for auditors). */
  creatorAddress: EthAddressSchema.nullable(),
})
export type CouncilListResponse = z.infer<typeof CouncilListResponseSchema>

/**
 * POST …/councils (paste path): a committee link `{COUNCIL_APP_URL}/c/0x…` or a bare id. 201 CouncilResponse with
 * `needsFingerprintConfirmation: true` (the existing row when the organization already linked it); 422
 * council_not_authorized when the ceremony does not authorize this organization's creator address; 404
 * unknown_committee.
 */
export const CouncilLinkRequestSchema = z.object({
  link: z.string().trim().min(1).max(500),
  label: z.string().trim().min(1).max(120).optional(),
})
export type CouncilLinkRequest = z.input<typeof CouncilLinkRequestSchema>

/**
 * POST …/councils/:councilId/confirm-fingerprint: the organizer re-states the fingerprint they checked with the
 * committee's organizer (id, t, n). The server refreshes the committee on chain and refuses (409
 * fingerprint_mismatch) unless every value matches.
 */
export const CouncilConfirmRequestSchema = z.object({
  cid: CeremonyIdSchema,
  threshold: z.number().int().min(1),
  size: z.number().int().min(1),
})
export type CouncilConfirmRequest = z.input<typeof CouncilConfirmRequestSchema>

/** PATCH …/councils/:councilId: rename (organization-local label only). */
export const CouncilUpdateRequestSchema = z.object({ label: z.string().trim().min(1).max(120) })
export type CouncilUpdateRequest = z.input<typeof CouncilUpdateRequestSchema>

/**
 * POST …/councils/pairing `{ setupId? }` → a fresh one-use code (60 min); any earlier unused code of the organization
 * dies. `setupId`: the committee being set up that this code connects (its name is then used).
 */
export const PairingCodeRequestSchema = z.object({ setupId: UuidSchema.optional() })
export type PairingCodeRequest = z.input<typeof PairingCodeRequestSchema>

export const PairingCodeResponseSchema = z.object({
  /** Grouped `XXXX-XXXX-XXXX`. Show it only inside this session; never put it in a link. */
  code: z.string(),
  expiresAt: IsoDateTimeSchema,
})
export type PairingCodeResponse = z.infer<typeof PairingCodeResponseSchema>

/* ================================================================== public pairing API (Council app) */

/**
 * GET /api/public/council-pairing/:code → PairingResolveResponse. Marks the code claimed (not used). 404 not_found
 * for an unknown, expired, used or superseded code (indistinguishable on purpose); 429 rate_limited; 503
 * council_unavailable when this server cannot vouch for its deployment (discovery failed / mismatch).
 */
export const PairingResolveResponseSchema = z.object({
  version: z.literal(1),
  orgId: UuidSchema,
  orgName: z.string(),
  /** The organization's creator address: authorize exactly this account (`authorizeCreator`). */
  creator: EthAddressSchema,
  chainId: z.number().int(),
  /** Council manager this server discovered from its registry (`registry.councilAdapter()` → `adapter.manager()`). */
  manager: EthAddressSchema,
  /** DAVINCI ProcessRegistry this server creates processes on (cross-check only: use your pinned registry). */
  registry: EthAddressSchema,
  /** Council adapter discovered from that registry (cross-check only: read it from your pinned registry). */
  adapter: EthAddressSchema,
  expiresAt: IsoDateTimeSchema,
})
export type PairingResolveResponse = z.infer<typeof PairingResolveResponseSchema>

/**
 * POST /api/public/council-pairing/:code/complete `{ cid, label? }` (after both grants). `label`: optional, the name
 * the committee's organizer gave the committee in the Council app (display only).
 */
export const PairingCompleteRequestSchema = z.object({
  cid: CeremonyIdSchema,
  label: z
    .string()
    .max(500)
    .optional()
    .transform((v) => {
      const out = v ? CommitteeLabelSchema.safeParse(v.slice(0, 80)) : null
      return out?.success ? out.data : undefined
    }),
})
export type PairingCompleteRequest = z.input<typeof PairingCompleteRequestSchema>

/**
 * → PairingCompleteResponse. The code is used up (whatever the verification says); the committee row is created
 * (or updated) for the organization and verified on chain: `status` is `ready` / `open` when Live with both grants,
 * else `forming` with `statusReason`. 404 not_found (unknown / expired / used / superseded / never resolved).
 * Committees are linked per organization: several organizations may link one ceremony, each usable only where the
 * ceremony authorized that organization's creator.
 */
export const PairingCompleteResponseSchema = z.object({
  ok: z.literal(true),
  status: CouncilStatusSchema,
  statusReason: z.string().nullable(),
  /** Path on this origin of the organization's committees tab (append to the pinned origin). */
  returnPath: z.string(),
})
export type PairingCompleteResponse = z.infer<typeof PairingCompleteResponseSchema>
